<script>
    // The card in focus (docs/phone-plan.md). A tap shows it at a medium size in
    // the half of the screen away from it, and a slim bar above the hand with its
    // name and the hinted action (the desktop's Play button); the rest of the
    // screen stays in use. Its manual actions (getActions, as Card.svelte's
    // popover), its tokens and what is beamed to it show only on a long press
    // (hold.js) or ⋯. The preview grows out of the tile tapped (zoom.js); a tap on
    // it, on the card again, or ✕ closes it.
    import { state, ui, prefs, res } from '../stores.js';
    import { getActions, isInPlayMarketNZ, allTokenTypes } from '../card_actions.js';
    import { getHints, autoActionButton, currentTraits } from '../parse.js';
    import { isMe } from '../net.js';
    import { fireCardAction } from '../card_fire.js';
    import TokenEdit from '../TokenEdit.svelte';
    import NewCardImage from '../newcards/NewCardImage.svelte';
    import PhoneTile from './PhoneTile.svelte';
    import { inspected, cardOf, offeredIds, tuckedUnder, CARD_RATIO } from './phone.js';
    import { faceImage, scanURL, thumbURL } from './face.js';
    import { zoomIn, closeInspector } from './zoom.js';
    import { haptic } from './haptics.js';

    let W = 375, H = 812;
    $: c = $inspected ? cardOf($inspected.id) : null;
    $: img = c ? faceImage(c) : null;
    $: manual = !!$inspected?.manual;
    $: prompting = offeredIds($ui).length > 0;

    // Recomputed whenever the board changes, as the desktop popover is on selection.
    $: hints = c && $state && !prompting && $prefs.showHints && $state.phase == 'action' && isMe($state.activePlayer) ? safeHints(c) : [];
    $: actions = c && $state && !prompting ? safeActions(c) : [];
    $: tokens = manual && c && $state && isInPlayMarketNZ(c) ? allTokenTypes() : [];
    $: under = manual && c ? tuckedUnder(c) : [];

    function safeHints(c){
        try { return getHints(c) || []; } catch(e){ console.log('getHints failed', e); return []; }
    }
    function safeActions(c){
        try { return getActions(c, c.facedown, true).filter(a => a.name && !a.hide); } catch(e){ console.log('getActions failed', e); return []; }
    }

    // Medium size, in the half of the screen away from the card, its buttons
    // under it: one small floating thing, so the rest of the screen stays in use.
    $: land = W > H;
    $: box = place($inspected?.from, W, H);
    function place(r, W, H){
        if(!r) return null;
        if(W > H){
            const h = Math.min(H - 120, 230), w = Math.round(h / CARD_RATIO);
            const right = r.left + r.width / 2 < W / 2;
            return { w, h, style: `top: calc(env(safe-area-inset-top, 0px) + 34px); ${right ? 'right' : 'left'}: calc(env(safe-area-inset-${right ? 'right' : 'left'}, 0px) + 10px);` };
        }
        const w = Math.min(180, Math.round(W * 0.48)), h = Math.round(w * CARD_RATIO);
        const left = Math.max(8, Math.min(W - w - 8, r.left + r.width / 2 - w / 2));
        const below = r.top + r.height / 2 < H / 2;
        return { w, h, style: `left: ${left}px; ` + (below ? `bottom: calc(env(safe-area-inset-bottom, 0px) + var(--phone-dock, 96px) + var(--phone-hand, 64px) + 8px);` : `top: calc(env(safe-area-inset-top, 0px) + var(--phone-top, 72px) + 8px);`) };
    }

    // The full scan replaces the thumbnail once it is in.
    let full = null;
    $: load(img);
    function load(name){
        full = null;
        if(!name) return;
        const im = new Image();
        im.onload = () => { if(img === name) full = scanURL(name); };
        im.src = scanURL(name);
    }

    let art, zoomed = null;
    $: if(art && $inspected && zoomed?.id !== $inspected.id){ zoomed = $inspected; zoomIn(art, $inspected.from); }
    $: if(!$inspected) zoomed = null;

    function act(run){
        haptic();
        closeInspector();
        run();
    }
</script>

<svelte:window bind:innerWidth={W} bind:innerHeight={H} />

{#if c && box}
    <div class="focus" style="width: {box.w}px; {box.style}" data-testid="phone-card-bar">
        <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
        <div class="art" bind:this={art} data-testid="phone-inspector" style="height: {box.h}px;" on:click={closeInspector}>
            {#if img}
                <img src={full ?? thumbURL(img)} alt={c.name ?? ''} draggable="false" />
            {:else}
                <div style="--width: {box.w}px; --height: {box.h}px;">
                    <NewCardImage {c} card={{...$res.atCards[c.id], traits: currentTraits(c), skills: c.values}} operations={$res.atOps[c.id]} handleClick={() => {}} />
                </div>
            {/if}
        </div>
        {#if hints.length || actions.length}
            <div class="btns">
                {#if hints.length}
                    <button class="primary" on:click={() => act(() => autoActionButton(c))}>{hints[0].name.includes('play') ? 'Play' : hints[0].name}</button>
                {/if}
                {#if !manual && actions.length}
                    <button class="more" aria-label="More actions" on:click={() => $inspected = { ...$inspected, manual: true }}>⋯</button>
                {/if}
            </div>
        {/if}
        {#if manual && (actions.length || tokens.length || under.length)}
            <div class="manual">
                {#each actions as a}
                    <button disabled={a.disabled || false} on:click={() => act(() => fireCardAction(c, a.type))}>{a.name}</button>
                {/each}
                {#if tokens.length}
                    <div class="tokens">{#each tokens as r}<TokenEdit card={true} p={c} {r} size={4.5} hideIfEmpty={false} />{/each}</div>
                {/if}
                {#if under.length}
                    <div class="under">{#each under as t (t.uniqueID)}<PhoneTile c={t} w={52} h={32} tucked={false} />{/each}</div>
                {/if}
            </div>
        {/if}
    </div>
{/if}

<style>
    .focus { position: fixed; z-index: 500; display: flex; flex-direction: column; gap: 5px; max-height: calc(100% - 90px); }
    .art { flex: none; border-radius: 10px; overflow: hidden; background: #111; box-shadow: 0 0 0 2px #fff7e6, 0 14px 36px rgba(0,0,0,.85); transform-origin: 0 0; cursor: pointer; }
    .art img { width: 100%; height: 100%; object-fit: cover; display: block; pointer-events: none; }
    .btns { display: flex; gap: 5px; }
    .btns .primary { flex: 1; }
    .manual { display: flex; flex-wrap: wrap; gap: 5px; padding: 5px; border-radius: 10px; background: #0f1a2c; border: 1px solid #2d4266; box-shadow: 0 8px 20px rgba(0,0,0,.6); overflow-y: auto; min-height: 0; }
    .manual button { flex: 1 1 45%; }
    .tokens, .under { display: flex; flex-wrap: wrap; gap: 5px; width: 100%; }
    .under { padding-top: 12px; }
    button { min-height: 34px; padding: 4px 10px; border-radius: 9px; border: 1px solid #2d4266; background: #1c2c48; color: #eef2f8; font: 700 14px 'Barlow Condensed', 'Arial Narrow', system-ui, sans-serif; touch-action: manipulation; box-shadow: 0 4px 12px rgba(0,0,0,.5); }
    button:disabled { opacity: .45; }
    button.primary { background: #f2c14e; border-color: #f2c14e; color: #1b1404; }
    button.more { min-width: 40px; padding: 4px 8px; }
</style>
